/**
 * profile-details.js
 */

import React from 'react';
import axios from 'axios';
import { Box, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { 
  emailValidationRules,
  firstNameValidationRules,
  lastNameValidationRules,
  phoneValidationRules,
  departmentNameValidationRules,
  badgeValidationRules,
  dutyValidationRules,
} from 'utils/user-profile-validation';
import { ThemeContext } from 'contexts/theme/theme-context';
import { UserContext } from 'contexts/user/user-context';
import { UserDataContext } from 'contexts/user-data/user-data-context';
import Button from 'components/button';
import ConfirmDialog from 'components/confirm-dialog';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import DrawerContent from 'components/menu-drawer/drawer-content';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import { EditorIconSection } from 'components/icon-section/icon-section';
import LabeledSwitch from 'components/labeled-switch/labeled-switch';
import MobileDrawerAppBar from 'components/menu-drawer/mobile/mobile-drawer-app-bar';
import TextField from 'components/form-inputs/text-field';


const useStyles = makeStyles(theme => ({
  contactLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  confirmEmailTitle: {
    [theme.breakpoints.down('sm')] : {
      fontSize: theme.typography.sizes[4]
    }
  },
  themeModeSection: {
    margin: 0,
    transformOrigin: 'top left',
  },
}));


const SupportChat = (props) => {
  const classes = useStyles();
  return (
    <span 
      className={classes.contactLink} 
      onClick={() => window.Intercom('showNewMessage')}>
        {props.children}
    </span>
  );
}


export default function UserEditDetails(props) {
  const {
    user,
    setUser,
    drawerState,
    onClose
  } = props;
  const { setTheme } = React.useContext(ThemeContext);
  const { user: currentUser, setUser: setCurrentUser } = React.useContext(UserContext);
  const { userData } = React.useContext(UserDataContext);
  const { enqueueSnackbar } = useSnackbar();
  // Add both user and userData to default values for initial form state
  const formContext = useForm({ defaultValues: {...user, data: {...userData}} });
  const { reset, handleSubmit, formState } = formContext;
  const { t } = useTranslation('profileEdit');
  const [openEmailChangeDialog, setOpenEmailChangeDialog] = React.useState(false);
  const [confirmEmailChange, setConfirmEmailChange] = React.useState();
  const classes = useStyles();
  const title = 'Details';


  const onSubmit = (formData) => {
    if (
      'firstName' in formState.dirtyFields 
      || 'lastName' in formState.dirtyFields 
      || 'email' in formState.dirtyFields
    ) {
      setOpenEmailChangeDialog(true);
    } else {
      save(formData);
    }   
  }


  const save = React.useCallback(async (formData) => {
    drawerState.loading.content.set(true);
    try {
      const response = await axios({
        method: 'PUT',
        url: `/account/${user.id}`,
        data: { 
          ...formData,
          data: { ...userData, ...formData.data }
        },
        bypassErrorHandling: true
      });

      const updatedUser = response.data;
      reset(updatedUser);
      setUser(updatedUser);
      enqueueSnackbar('Profile saved', { variant: 'success' });

      if (user.id === currentUser.id) {
        setCurrentUser(updatedUser);
        setTheme(updatedUser.data?.theme?.isDarkMode ? Types.Theme.DARK : Types.Theme.LIGHT);
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: 'error' });
    } finally {
      drawerState.loading.content.set(false);
    }
  }, [
    drawerState, 
    user, 
    userData,
    setUser, 
    currentUser, 
    setTheme,
    setCurrentUser, 
    enqueueSnackbar, 
    reset,
  ]);


  React.useEffect(() => {
    const emailChange = async () => {
      const confirmed = await confirmEmailChange();
      if (confirmed) {
        handleSubmit(save)();
      } else {
        reset(user);
      }

      setOpenEmailChangeDialog(false);
      setConfirmEmailChange();
    }

    if (confirmEmailChange) {
      emailChange();
    }  
  }, [confirmEmailChange, handleSubmit, save, reset, user]);


  const cancelButton = (
    <Button upperCase={false} onClick={onClose}>Cancel</Button>
  );


  const saveButton = (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty}
      style={{ marginLeft: 16 }}
      onClick={() => handleSubmit(onSubmit)()}
      children='Save'
    />
  );

  
  return <>
    <DrawerContentBuilder
      mobileAppBar={{ content: (
        <MobileDrawerAppBar
          title={title}
          saveButton={saveButton}
          onClose={onClose}
        />
      )}}
      desktopHeader={{
        title,
        actions: <>{cancelButton}{saveButton}</>
      }}
      stickyHeader
      children={(
        <FormProvider {...formContext}>
          <form>

            <DrawerContent>
              <Alert severity="info" style={{marginBottom: 16}}>
                <Typography paragraph>
                  To protect record integrity, the name and department fields can't be changed. 
                  If you need to make a change to these fields then use 
                  the <strong><SupportChat>SUPPORT CHAT</SupportChat></strong> to
                  let us know and we'll correct it for you. If you want to transfer your 
                  subscription to another user then reach out to us using the support chat. If
                  you're moving to a different department then you must also start a new account.
                </Typography>
                <Typography>
                  WARNING: Using a single account for different handlers (even for the same K9) or using the 
                  same account across different departments will damage the integrity of your records. 
                </Typography>
              </Alert>

              <EditorIconSection 
                iconName='profile' 
                helpProps={{
                  translation: 'profileEdit',
                  section: 'general'
                }}
              >
                {/* FIRST & LAST */}
                <HelpTextItem
                  sidebarText={drawerState.sidebarText}
                  title={t('general.name.helpTitle')}
                  text={t('general.name.helpInfo')}
                >
                  {focusHandlers =>
                    <Grid container spacing={2}>
                      <FormGridItem>
                        <TextField
                          name='firstName'
                          label={t('general.name.firstNameLabel')}
                          focusHandlers={focusHandlers}
                          disabled={user.firstName !== ''}
                          rules={firstNameValidationRules(
                            t('general.name.errMsgEmpty'), 
                            t('errMsgLength', { maxLength: '50' })
                          )}
                        />
                      </FormGridItem>
                      <FormGridItem>
                        <TextField
                          name='lastName'
                          label={t('general.name.lastNameLabel')}
                          focusHandlers={focusHandlers}
                          disabled={user.lastName !== ''}
                          rules={lastNameValidationRules(
                            t('general.name.errMsgEmpty'), 
                            t('errMsgLength', { maxLength: '50' })
                          )}
                        />
                      </FormGridItem>
                    </Grid>}
                </HelpTextItem>

                {/* EMAIL */}
                <HelpTextItem
                  sidebarText={drawerState.sidebarText}
                  title={t('general.email.helpTitle')}
                  text={t('general.email.helpInfo')}
                >
                  {focusHandlers =>
                    <Grid container>
                      <FormGridItem>
                        <TextField
                          name='email'
                          label={t('general.email.label')}
                          focusHandlers={focusHandlers}
                          rules={emailValidationRules(
                            t('general.email.errMsgEmpty'), 
                            t('general.email.errMsgInvalid'), 
                            t('errMsgLength', { maxLength: '200' })
                          )}
                        />
                      </FormGridItem>
                    </Grid>}
                </HelpTextItem>

                {/* PHONE NUMBER */}
                <HelpTextItem
                  sidebarText={drawerState.sidebarText}
                  title={t('general.phone.helpTitle')}
                  text={t('general.phone.helpInfo')}
                >
                  {focusHandlers =>
                    <Grid container>
                      <FormGridItem>
                        <TextField
                          name='phone'
                          label={t('general.phone.label')}
                          focusHandlers={focusHandlers}
                          rules={phoneValidationRules(t('errMsgLength', { maxLength: '15' }))}
                        />
                      </FormGridItem>
                    </Grid>}
                </HelpTextItem>

              </EditorIconSection>

              <EditorIconSection 
                iconName="department" 
                helpProps={{
                  translation: 'profileEdit',
                  section: 'department'
                }}
              >

                {/* DEPARTMENT NAME */}
                <HelpTextItem
                  sidebarText={drawerState.sidebarText}
                  title={t('department.name.helpTitle')}
                  text={t('department.name.helpInfo')}
                >
                  {focusHandlers =>
                    <Grid container>
                      <FormGridItem>
                        <TextField
                          name='departmentName'
                          label={t('department.name.label')}
                          focusHandlers={focusHandlers}
                          disabled={user.departmentName !== ''}
                          rules={departmentNameValidationRules(
                            t('department.name.errMsgEmpty'),
                            t('errMsgLength', { maxLength: '250' })
                          )}
                        />
                      </FormGridItem>
                    </Grid>}
                </HelpTextItem>

                {/* BADGE */}
                <HelpTextItem
                  sidebarText={drawerState.sidebarText}
                  title={t('department.badge.helpTitle')}
                  text={t('department.badge.helpInfo')}
                >
                  {focusHandlers =>
                    <Grid container>
                      <FormGridItem>
                        <TextField
                          name='badge'
                          label={t('department.badge.label')}
                          focusHandlers={focusHandlers}
                          rules={badgeValidationRules(
                            t('department.badge.errMsgEmpty'),
                            t('errMsgLength', { maxLength: '250' })
                          )}
                        />
                      </FormGridItem>
                    </Grid>}
                </HelpTextItem>

                {/* DUTY ASSIGNMENT */}
                <HelpTextItem
                  sidebarText={drawerState.sidebarText}
                  title={t('department.dutyAssignment.helpTitle')}
                  text={t('department.dutyAssignment.helpInfo')}
                >
                  {focusHandlers =>
                    <Grid container>
                      <FormGridItem>
                      <TextField
                        name='dutyAssignment'
                        label={t('department.dutyAssignment.label')}
                        focusHandlers={focusHandlers}
                        rules={dutyValidationRules(t('errMsgLength', { maxLength: '250' }))}
                        />
                      </FormGridItem>
                    </Grid>}
                </HelpTextItem>

              </EditorIconSection>

              <EditorIconSection 
                iconName="dark-mode-toggle" 
                iconMarginTop={0}
                helpProps={{
                  translation: 'profileEdit',
                  section: 'theme'
                }}
              >

                {/* THEME MODE */}
                <HelpTextItem
                  sidebarText={drawerState.sidebarText}
                  title={t('theme.mode.helpTitle')}
                  text={t('theme.mode.helpInfo')}
                >
                  {focusHandlers =>
                    <Grid container>
                      <Grid item xs={12}>
                        <FormSectionTitle style={{ marginBottom: 8 }}>{t('theme.mode.label')}</FormSectionTitle>
                        <Box mt={1} mb={1.5}>
                          <Box className={classes.themeModeSection}>
                            <Controller
                              control={formContext.control}
                              name='data.theme.isDarkMode'
                              defaultValue={false} // light
                              render={({ field }) =>
                                <LabeledSwitch
                                  {...field}
                                  checked={Boolean(field.value)}
                                  onChange={e => {
                                    field.onChange(e.target.checked);
                                  }}
                                  labels={[t('theme.mode.lightChoice'), t('theme.mode.darkChoice')]}
                                  style={{
                                    margin: 0,
                                  }}
                                />
                              }
                            >
                            </Controller>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>}
                </HelpTextItem>

              </EditorIconSection>

              {/* REPORT OPTIONS */}
              {(currentUser.role.isHandler || currentUser.role.isSupervisor) && (
                <EditorIconSection 
                  iconName="report" 
                  iconMarginTop={0}
                  helpProps={{
                    translation: 'profileEdit',
                    section: 'reportOptions'
                  }}
                >
                  <HelpTextItem
                    sidebarText={drawerState.sidebarText}
                    title={t('reportOptions.demographic.helpTitle')}
                    text={t('reportOptions.demographic.helpInfo')}
                  >
                    {focusHandlers =>
                      <Grid container>
                        <Grid item xs={12}>
                          <FormSectionTitle style={{ marginBottom: 8 }}>{t('reportOptions.demographic.label')}</FormSectionTitle>
                          <Box mt={1} mb={1.5}>
                            <Controller
                              control={formContext.control}
                              name='data.reports.showDemographicData'
                              defaultValue={true}
                              render={({ field }) =>
                                <LabeledSwitch
                                  {...field}
                                  checked={Boolean(field.value)}
                                  onChange={e => {
                                    field.onChange(e.target.checked);
                                  }}
                                  labels={[t('reportOptions.demographic.hideChoice'), t('reportOptions.demographic.showChoice')]}
                                  style={{
                                    margin: 0,
                                  }}
                                />
                              }
                            >
                            </Controller>
                          </Box>
                        </Grid>
                      </Grid>}
                  </HelpTextItem>
                </EditorIconSection>
              )}

            </DrawerContent>
          </form>
        </FormProvider>
      )}
    />

    <ConfirmUnsavedEditor 
      dirty={formState.isDirty}
      save={async () => handleSubmit(onSubmit)()}
    />

    {openEmailChangeDialog &&
    <ConfirmDialog 
      content={
        <>
          {`${t('confirmEmailChange')} Please `}
          <span 
            children='contact us'
            className={classes.contactLink} 
            onClick={() => { 
              reset(user);
              setOpenEmailChangeDialog(false);
              setConfirmEmailChange();
              window.Intercom('showNewMessage');
            }}
          />
          &nbsp;to arrange this.
        </>}
      title={<span className={classes.confirmEmailTitle}>Confirm Name or Email Change</span>}
      titleIcon='info'
      setConfirm={setConfirmEmailChange}
      customActions={[
        {
          label: 'Cancel',
          value: false,
          isNotPriority: true,
        },
        {
          label: 'Confirm',
          value: true,
        },
      ]}
    />}
  </>;
}